import { EventContentArg, EventInput } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import FullCalendar from '@fullcalendar/react';
import { useSubnavResizeListener } from '@monorepo/shared/hooks/subnav/useSubnavResizeListener';
import { ComplianceCalendarEvent } from 'mapistry-shared';
import React, { useCallback } from 'react';
import { useComplianceCalendarContext } from '../ComplianceCalendarContext';
import {
  CalendarViewType,
  getComplianceCalendarNow,
} from '../helpers/complianceCalendarHelpers';
import { sortEventsWithinDay } from '../helpers/sortComplianceCalendarEventsHelper';

const headerToolbar = { start: '', center: '', end: '' };
const plugins = [dayGridPlugin];

type ComplianceCalendarMonthViewProps = {
  calendarRange: { end: Date };
  eventDataTransform: (backendEvent: EventInput) => EventInput;
  events: ComplianceCalendarEvent[] | undefined;
  isLoading: boolean;
  loadingEvents: EventInput[];
  renderEventContent: (eventInfo: EventContentArg) => React.ReactNode;
};

export function ComplianceCalendarMonthView({
  calendarRange,
  eventDataTransform,
  events,
  isLoading,
  loadingEvents,
  renderEventContent,
}: ComplianceCalendarMonthViewProps) {
  const { calendarRef, visibleRangeStart, onDatesSet } =
    useComplianceCalendarContext();

  const calendarApi = calendarRef?.current?.getApi();
  const resizeCalendarAfterNavFinishedResizing = useCallback(() => {
    calendarApi?.updateSize();
  }, [calendarApi]);

  useSubnavResizeListener(resizeCalendarAfterNavFinishedResizing);

  return (
    <FullCalendar
      eventOrder={sortEventsWithinDay}
      moreLinkClassNames={isLoading ? 'more-link-is-loading' : ''}
      moreLinkClick={() => {
        setTimeout(() =>
          document
            .querySelector<HTMLElement>('.fc-more-popover [tabindex="0"]')
            // show the focus ring even when "more" was clicked with a mouse
            ?.focus({ focusVisible: true } as FocusOptions),
        );
        return 'popover'; // the default option moreLinkClick returns
      }}
      datesSet={onDatesSet}
      views={{
        [CalendarViewType.Month]: { dayMaxEvents: true }, // show the "+n more" button
        // used briefly when switching to the custom week view
        [CalendarViewType.Week]: { dayMaxEvents: false },
      }}
      defaultAllDay // prevents us from having to specify start and end time
      eventContent={renderEventContent}
      eventDataTransform={eventDataTransform}
      eventDisplay="list-item" // show events with a little dot instead of block
      events={isLoading ? loadingEvents : events}
      headerToolbar={headerToolbar} // we're displaying custom ones instead
      initialDate={visibleRangeStart}
      initialView={CalendarViewType.Month}
      lazyFetching={false} // let us manage the cache in react query manually instead of hooking into full calendar
      now={getComplianceCalendarNow}
      plugins={plugins}
      ref={calendarRef}
      showNonCurrentDates={false} // don't show the days from the previous and next month
      timeZone="UTC"
      validRange={calendarRange}
    />
  );
}
